/**
 * D-1 出行人 —— `/api/travelers`（契约 §7 D-1，2026-09-03 已建成）。
 *
 * 沿用 `http.ts` 那一套（`x-trace-id`、401 单飞刷新都在里面），本层只拼路径与 schema。
 *
 * ★ 明文纪律：`create` / `update` 的 `body.docNo` / `body.phone` 是明文 ——
 *   HTTP 层已保证只走一条网关、走 HTTPS；本层**不做任何缓存、不打日志**。
 * ⚠ 全部端点都要登录（网关「匿名禁止 `/api/travelers/**`」），所以用默认 `auth: 'required'`。
 */
import {
  travelerListSchema,
  travelerSummarySchema,
  voidDataSchema,
  type TravelerSummary,
  type TravelerUpsertRequest,
} from '@cione/contracts'
import type { HttpClient } from './http'

export interface TravelersApi {
  /**
   * 花名册。⚠ **没有分页**：只有 `keyword` + `limit`（服务端默认 20、上限 200）。
   *   别按「有总数、能翻页」设计界面 —— proto 里根本没有 offset/游标。
   */
  list(params?: { keyword?: string; limit?: number }): Promise<TravelerSummary[]>
  /** 新建。⚠ 该必填的服务端会校验（新建路径没有「留空=保持不变」的放松）。 */
  create(body: TravelerUpsertRequest): Promise<TravelerSummary>
  /**
   * 编辑。⚠⚠ **只发用户真改过的字段**：后端「留空 = 保持不变」。
   *   绝不能把摘要里的 `docNumberMasked` / `phoneMasked` 回填再提交 —— 会吃 400。
   */
  update(travelerId: string, body: TravelerUpsertRequest): Promise<TravelerSummary>
  /** 删除 = **归档**（不是物理删）。已有订单的退改还用得到它，文案别写「永久删除」。 */
  remove(travelerId: string): Promise<void>
}

export function createTravelersApi(http: HttpClient): TravelersApi {
  return {
    list(params) {
      return http.request({
        method: 'GET',
        path: '/api/travelers',
        query: { keyword: params?.keyword, limit: params?.limit },
        schema: travelerListSchema,
      })
    },

    create(body) {
      return http.request({
        method: 'POST',
        path: '/api/travelers',
        body,
        schema: travelerSummarySchema,
      })
    },

    update(travelerId, body) {
      return http.request({
        method: 'PUT',
        path: `/api/travelers/${encodeURIComponent(travelerId)}`,
        body,
        schema: travelerSummarySchema,
      })
    },

    async remove(travelerId) {
      await http.request({
        method: 'DELETE',
        path: `/api/travelers/${encodeURIComponent(travelerId)}`,
        schema: voidDataSchema,
      })
    },
  }
}
